import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { BAND_UI, CHILD_COLORS } from "@/lib/bands";
import { FrogFace } from "@/components/FrogArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, BarChart3, LogOut, Copy, Pencil } from "lucide-react";

export default function HouseholdHome() {
  const nav = useNavigate();
  const code = localStorage.getItem("frogfirst_code");
  const [hh, setHh] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [kidName, setKidName] = useState("");
  const [band, setBand] = useState("k2");
  const [mode, setMode] = useState("frog");
  const [editKid, setEditKid] = useState(null);
  const [editName, setEditName] = useState("");
  const [editMode, setEditMode] = useState("frog");

  const load = useCallback(async () => {
    try {
      setHh(await api.getHousehold(code));
    } catch {
      localStorage.removeItem("frogfirst_code");
      nav("/");
    }
  }, [code, nav]);

  useEffect(() => {
    if (!code) return nav("/");
    load();
  }, [code, nav, load]);

  const addKid = async () => {
    if (!kidName.trim()) return;
    const color = CHILD_COLORS[(hh?.children?.length || 0) % CHILD_COLORS.length];
    await api.addChild(code, { name: kidName.trim(), age_band: band, color, mode });
    toast.success(`${kidName} joined the pond!`);
    setKidName("");
    setAddOpen(false);
    load();
  };

  const openEdit = (kid) => {
    setEditKid(kid);
    setEditName(kid.name);
    setEditMode(kid.mode || "frog");
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    await api.updateChild(editKid.id, { name: editName.trim(), mode: editMode });
    toast.success("Saved!");
    setEditKid(null);
    load();
  };

  if (!hh) return <div className="min-h-screen bg-[#F0FDF4]" />;

  return (
    <div className="min-h-screen bg-[#F0FDF4]">
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-white/40 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FrogFace size={40} />
            <div>
              <p className="font-display font-bold text-slate-800 leading-tight">
                <span className="bg-gradient-to-br from-sky-500 to-blue-500 bg-clip-text text-transparent">Aqua</span>
                <span className="text-green-600"> Tadpole</span>
                <span className="text-slate-400 font-normal"> · {hh.name}</span>
              </p>
              <button
                data-testid="copy-code-btn"
                onClick={() => { navigator.clipboard.writeText(hh.code); toast.success("Code copied!"); }}
                className="text-xs text-slate-500 flex items-center gap-1 hover:text-sky-600"
              >
                Code: <span className="font-mono font-bold tracking-widest">{hh.code}</span> <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="parent-dashboard-btn" onClick={() => nav("/parent")} variant="outline" className="rounded-xl font-semibold border-2">
              <BarChart3 className="w-4 h-4 mr-2" /> Parent view
            </Button>
            <Button
              data-testid="leave-household-btn"
              variant="ghost"
              onClick={() => { localStorage.removeItem("frogfirst_code"); nav("/"); }}
              className="rounded-xl text-slate-400"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-800">Who's <span className="text-sky-600">catching</span> a frog today?</h1>
        <p className="text-slate-500 mt-2">Tap your name to see today's frog.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {hh.children.map((kid, i) => (
            <motion.div
              key={kid.id}
              data-testid={`kid-card-${kid.name.toLowerCase().replace(/\s/g, "-")}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => nav(`/kid/${kid.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && nav(`/kid/${kid.id}`)}
              className="btn-tactile bg-white rounded-3xl p-8 border border-slate-100 text-left group cursor-pointer relative"
            >
              <button
                data-testid={`edit-kid-btn-${kid.name.toLowerCase().replace(/\s/g, "-")}`}
                onClick={(e) => { e.stopPropagation(); openEdit(kid); }}
                title="Edit kid"
                className="absolute top-5 right-5 w-9 h-9 rounded-xl bg-slate-50 text-slate-400 hover:bg-green-100 hover:text-green-600 flex items-center justify-center transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-display text-2xl font-bold text-white" style={{ backgroundColor: kid.color }}>
                {kid.name[0].toUpperCase()}
              </div>
              <p className="font-display text-xl font-bold text-slate-800 mt-4 group-hover:text-green-600">{kid.name}</p>
              <p className="text-sm text-slate-500 mt-1">
                {BAND_UI[kid.age_band]?.label} • {kid.mode === "tadpole" ? "Multiple Tadpoles" : BAND_UI[kid.age_band]?.kidTitle}
              </p>
            </motion.div>
          ))}

          <motion.button
            data-testid="add-child-btn"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: hh.children.length * 0.08 }}
            onClick={() => setAddOpen(true)}
            className="rounded-3xl p-8 border-4 border-dashed border-green-200 text-left hover:border-green-400 hover:bg-green-50 transition-colors"
          >
            <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
              <Plus className="w-8 h-8 text-green-500" />
            </div>
            <p className="font-display text-xl font-bold text-green-600 mt-4">Add a kid</p>
            <p className="text-sm text-slate-500 mt-1">Each kid gets their own 14-day pond</p>
          </motion.button>
        </div>
      </main>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a kid</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <Input
              data-testid="kid-name-input"
              value={kidName}
              onChange={(e) => setKidName(e.target.value)}
              placeholder="Kid's first name"
              className="h-14 rounded-xl text-lg"
            />
            <div>
              <p className="text-sm font-bold text-slate-600 uppercase tracking-widest mb-3">Age band</p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(BAND_UI).map(([key, b]) => (
                  <button
                    key={key}
                    data-testid={`band-option-${key}`}
                    onClick={() => setBand(key)}
                    className={`rounded-2xl p-4 text-left border-2 transition-colors ${
                      band === key ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"
                    }`}
                  >
                    <p className="font-display font-bold text-slate-800">{b.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{b.kidTitle}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-600 uppercase tracking-widest mb-3">Program mode</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="mode-option-frog"
                  onClick={() => setMode("frog")}
                  className={`rounded-2xl p-4 text-left border-2 transition-colors ${mode === "frog" ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"}`}
                >
                  <p className="font-display font-bold text-slate-800">One Frog</p>
                  <p className="text-xs text-slate-500 mt-1">One hardest task a day. Simple.</p>
                </button>
                <button
                  data-testid="mode-option-tadpole"
                  onClick={() => setMode("tadpole")}
                  className={`rounded-2xl p-4 text-left border-2 transition-colors ${mode === "tadpole" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}
                >
                  <p className="font-display font-bold text-slate-800">Multiple Tadpoles</p>
                  <p className="text-xs text-slate-500 mt-1">Daily homework turn-ins: frog first, then check off the tadpoles.</p>
                </button>
              </div>
            </div>
            <Button data-testid="confirm-add-kid-btn" onClick={addKid} disabled={!kidName.trim()} className="btn-tactile w-full h-14 rounded-xl bg-green-500 hover:bg-green-600 font-display font-semibold text-lg">
              Add to the pond
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editKid} onOpenChange={(o) => !o && setEditKid(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit {editKid?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <Input
              data-testid="edit-kid-name-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Kid's first name"
              className="h-14 rounded-xl text-lg"
            />
            <div>
              <p className="text-sm font-bold text-slate-600 uppercase tracking-widest mb-3">Program mode</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="edit-mode-option-frog"
                  onClick={() => setEditMode("frog")}
                  className={`rounded-2xl p-4 text-left border-2 transition-colors ${editMode === "frog" ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"}`}
                >
                  <p className="font-display font-bold text-slate-800">One Frog</p>
                  <p className="text-xs text-slate-500 mt-1">One hardest task a day. Simple.</p>
                </button>
                <button
                  data-testid="edit-mode-option-tadpole"
                  onClick={() => setEditMode("tadpole")}
                  className={`rounded-2xl p-4 text-left border-2 transition-colors ${editMode === "tadpole" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-sky-300"}`}
                >
                  <p className="font-display font-bold text-slate-800">Multiple Tadpoles</p>
                  <p className="text-xs text-slate-500 mt-1">Frog first, then check off the tadpoles.</p>
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Mode changes apply from the next frog pick — today's frog stays as is.</p>
            </div>
            <Button data-testid="save-edit-kid-btn" onClick={saveEdit} disabled={!editName.trim()} className="btn-tactile w-full h-14 rounded-xl bg-green-500 hover:bg-green-600 font-display font-semibold text-lg">
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
